import { useState, useEffect } from "react";
// prop-types is library for typechecking of props
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

// sweetalert2 components
import Swal from "sweetalert2";

import { getData, postData } from "Utils/server";
import { getAuthorizationHeader, getUserData } from "Utils/helpers";

function not(a, b) {
  return a.filter((a_item) => (b.find((b_item) => b_item.id == a_item.id) ? false : true));
}

function intersection(a, b) {
  return a.filter((a_item) => (b.find((b_item) => b_item.id == a_item.id) ? true : false));
}

function union(a, b) {
  return [...a, ...not(b, a)];
}

function TransferList({ userId, informParent, checkChanges }) {
  const userData = getUserData();
  const manager = userData.id;
  const [initializing, setInitializing] = useState(false);
  const [checked, setChecked] = useState([]);
  const [disabledChecked, setDisableChecked] = useState([]);
  const [left, setLeft] = useState([]);
  const [right, setRight] = useState([]);
  const [userPlatforms, setUserPlatforms] = useState("");
  const [loading, setLoading] = useState(false);
  const [disableSave, setDisableSave] = useState(false);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = (item) => () => {
    if (item.disabled) return;
    const currentIndex = checked.findIndex((checkedItem) => checkedItem.id == item.id); //checked.indexOf(item.id);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(item);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const numberOfChecked = (items) =>
    intersection(checked, items).length == 0
      ? intersection(checked, items).length
      : intersection(checked, items).length + disabledChecked.length;

  const handleToggleAll = (items) => () => {
    let itemsToUse = [...items];
    if (numberOfChecked(itemsToUse) === itemsToUse.length) {
      setDisableChecked(itemsToUse.filter((item) => item.disabled));
      itemsToUse = itemsToUse.filter((item) => !item.disabled);
      setChecked(not(checked, itemsToUse));
    } else {
      setDisableChecked(itemsToUse.filter((item) => item.disabled));
      itemsToUse = itemsToUse.filter((item) => !item.disabled);
      setChecked(union(checked, itemsToUse));
    }
  };

  const handleCheckedRight = () => {
    let checkedForRight = [...leftChecked];
    let checkedString = JSON.stringify(checked);
    let leftCheckedString = JSON.stringify(leftChecked);

    if (
      (checked.length > 0 && leftChecked.length == 0) ||
      checked.length != leftChecked.length ||
      checkedString != leftCheckedString
    ) {
      checkedForRight = [...checked];
      for (let index = 0; index < checkedForRight.length; index++) {
        const permission = checkedForRight[index];
        permission.disabled = leftChecked.find((item) => item.id == permission.id) ? false : true;
      }
    }
    setRight(right.concat(checkedForRight));
    setLeft(not(left, checkedForRight));
    setChecked(not(checked, checkedForRight));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const customList = (title, items) => (
    <Card>
      <CardHeader
        sx={{ px: 2, py: 1, marginTop: "10px" }}
        avatar={
          <Checkbox
            onClick={handleToggleAll(items)}
            checked={numberOfChecked(items) === items.length && items.length !== 0}
            indeterminate={numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0}
            disabled={items.length === 0}
            inputProps={{
              "aria-label": "all items selected",
            }}
          />
        }
        title={title}
        subheader={`${numberOfChecked(items)}/${items.length} selected`}
      />
      <Divider />
      <List
        sx={{
          width: "100%",
          height: 230,
          bgcolor: "background.paper",
          overflowY: "auto",
          overflowX: "hidden",
          padding: "10px",
          marginBottom: "20px",
          minWidth: "max-content",
          scrollbarWidth: "thin",
        }}
        dense
        component="div"
        role="list"
      >
        {items.map((item) => {
          const labelId = `transfer-list-all-item-${item.id}-label`;

          return (
            <ListItem
              key={item.id}
              role="listitem"
              button
              onClick={handleToggle(item)}
              sx={{ width: "100%", marginRight: "10px" }}
            >
              <ListItemIcon sx={{ minWidth: "5px" }}>
                <Checkbox
                  checked={checked.find((checkedItem) => checkedItem.id == item.id) ? true : false}
                  disabled={item.disabled}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{
                    "aria-labelledby": labelId,
                  }}
                />
              </ListItemIcon>
              <ListItemText
                id={labelId}
                primary={`${item.name}`}
                className={item.disabled ? "list-item-disabled" : "list-item"}
              />
            </ListItem>
          );
        })}
      </List>
    </Card>
  );

  async function loadData() {
    setLoading(true);
    const getPlatformsEndpoint = `/api/v1/users/${manager}/platforms`;
    const available_response = await getData(getPlatformsEndpoint, {
      Authorization: getAuthorizationHeader(),
    });

    if (available_response.data != null) {
      setLeft(available_response.data);
    }

    let getUserPlatformsEndPoint = `/api/v1/users/${userId}/subplatforms`;
    let user_response = await getData(getUserPlatformsEndPoint, {
      Authorization: getAuthorizationHeader(),
    });

    if (user_response != null) {
      setUserPlatforms(JSON.stringify(user_response.data));
      setChecked(user_response.data);
      if (user_response.data.length == 0) setInitializing(null);
    }
    setLoading(false);
  }

  const savePlatforms = async () => {
    const postPlatformsEndpoint = `/api/v1/users/${userId}/platforms`;

    let platformsToSave = [...right];
    let platformsToCheck = [...right];
    platformsToCheck = platformsToCheck.filter((item) => !item.disabled);
    if (platformsToCheck.length == 0) return;

    const setPlatformsResponse = await postData(postPlatformsEndpoint, platformsToSave, {
      Authorization: getAuthorizationHeader(),
    });

    if (setPlatformsResponse) {
      Swal.fire("Request Sent!", `User's Platforms Updated sucessfully!`, "success");
      setUserPlatforms(JSON.stringify(right));
      informParent("update", "refresh");
    } else {
      Swal.fire(
        "Error!",
        `Something goes wrong on User's Platforms Update. Please try again later.`,
        "error"
      );
      loadData();
    }
  };

  useEffect(() => {
    if (checkChanges) {
      if (userPlatforms != JSON.stringify(right) && !initializing && !loading) {
        let platforms = [...right];

        platforms = platforms.filter((item) => !item.disabled);
        if (platforms.length > 0) {
          setDisableSave(false);
          informParent(true);
        } else {
          setDisableSave(true);
          informParent(false);
        }
      } else {
        informParent(false);
      }
    }
  }, [checkChanges]);

  useEffect(() => {
    if (checked.length > 0 && (left.length > 0 || right.length > 0) && initializing == false) {
      setInitializing(true);
    }
  }, [checked]);

  useEffect(() => {
    if (userId) {
      loadData();
    }
  }, [userId]);

  useEffect(() => {
    if (initializing == true) {
      handleCheckedRight();
      setInitializing(null);
    }
  }, [initializing]);

  return (
    <>
      <Grid container spacing={2} justifyContent="center" alignItems="center">
        <Grid item sx={{ minWidth: "30%" }}>
          {customList("Available Platforms", left)}
        </Grid>
        <Grid item>
          <Grid container direction="column" alignItems="center">
            <Button
              sx={{ my: 0.5, minWidth: "30px", padding: "5px" }}
              variant="outlined"
              size="small"
              onClick={handleCheckedRight}
              disabled={leftChecked.length === 0}
              aria-label="move selected right"
            >
              &gt;
            </Button>
            <Button
              sx={{ my: 0.5, minWidth: "30px", padding: "5px" }}
              variant="outlined"
              size="small"
              onClick={handleCheckedLeft}
              disabled={rightChecked.length === 0}
              aria-label="move selected left"
            >
              &lt;
            </Button>
          </Grid>
        </Grid>
        <Grid item sx={{ minWidth: "30%" }}>
          {customList("Given Platforms", right)}
        </Grid>
      </Grid>
      <Grid sx={{ marginTop: "20px" }}>
        <SoftBox
          ml="auto"
          display="flex"
          justifyContent="right"
          alignItems="flex-end"
          sx={{ borderTop: 1, borderColor: "divider", paddingTop: "24px" }}
        >
          <SoftButton variant="gradient" color="dark" size="small" onClick={savePlatforms}>
            update platforms
          </SoftButton>
        </SoftBox>
      </Grid>
    </>
  );
}

// Typechecking props for the ProfileInfoCard
TransferList.propTypes = {
  userId: PropTypes.number.isRequired,
  informParent: PropTypes.func,
  checkChanges: PropTypes.bool,
};

export default TransferList;
